/* eslint-disable react/prop-types */
import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Input, message, Select, Typography } from 'antd';

import { InfoCircleOutlined, ReloadOutlined, SendOutlined, SwapOutlined } from '@ant-design/icons';

import Utils from '../../../../Assets/Scripts/Utils';
import { api } from '../../../../Services/axiosService';

import MessageList from './MessageList';

import './Communication.scss';

const { Option } = Select;
const { Title } = Typography;

function CommunicationPanel({ task, isTaskReadOnly, fieldsPermissions, isFocused }) {
  const [loading, setLoading] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [activeType, setActiveType] = useState('Instructor');
  const [messages, setMessages] = useState({
    Instructor: [],
    Transport: [],
  });
  const userData = JSON.parse(localStorage.getItem('conecta__userData') || '{}');
  const messageInputRef = useRef(null);

  const isTransportCompanyUser = userData.isTransportCompany === true;
  const isOperatorUser = task.scheduleType;
  const hasTransportIncluded = task?.trainingTransportation === 'Incluso';
  const scheduleType = task.scheduleType?.toLowerCase();

  let isFinished = false;
  let isAccepted = true;
  if (scheduleType === 'driver') {
    const stage = task.driverList.find((driver) => driver.userId === userData.id)?.stage;
    isFinished = task.stage.id === 5 || stage?.id === 5;
    isAccepted = stage?.id === 3;
  } else if (scheduleType?.includes('instructor')) {
    isFinished = task.stage.id === 5 || task[task.scheduleType]?.stage?.id === 5;
    isAccepted = task[task.scheduleType]?.stage?.id === 3;
  }

  useEffect(() => {
    if (isTransportCompanyUser && hasTransportIncluded) {
      setActiveType('Transport');
    } else if (!hasTransportIncluded && activeType === 'Transport') {
      setActiveType('Instructor');
    }

    if (isFocused && messageInputRef.current) {
      messageInputRef.current.focus();
    }
  }, [isTransportCompanyUser, hasTransportIncluded, activeType, isFocused]);

  const communicationAccessLevel = fieldsPermissions?.Communication?.access || 0;
  const canWrite = communicationAccessLevel === 2 && !isTaskReadOnly && !isFinished && isAccepted;

  const canEffectivelySendToInstructor = !!task?.mainInstructor?.userId;
  const canEffectivelySendToTransport = !!task?.transportCompany?.id && hasTransportIncluded;

  let isUserLinkedToTaskTransport = false;
  if (isTransportCompanyUser && task?.transportCompany?.id && userData.transportCompanyList) {
    const userTransportCompanies = userData.transportCompanyList
      .split(',')
      .map((id) => parseInt(id.trim(), 10));
    if (userTransportCompanies.includes(task.transportCompany.id)) {
      isUserLinkedToTaskTransport = true;
    }
  }

  const isMessagingDisabled =
    !canWrite ||
    (activeType === 'Instructor' && !canEffectivelySendToInstructor) ||
    (activeType === 'Transport' &&
      (!canEffectivelySendToTransport || (isTransportCompanyUser && !isUserLinkedToTaskTransport)));

  let showRefreshButton = false;
  if (activeType === 'Instructor' && canEffectivelySendToInstructor) {
    showRefreshButton = true;
  } else if (activeType === 'Transport' && canEffectivelySendToTransport) {
    showRefreshButton = true;
  }
  showRefreshButton = showRefreshButton && canWrite;

  const fetchMessages = async () => {
    try {
      setLoading(true);

      const instructorMessagesPromise = api.get(
        `/Communication?filters[0].Field=TaskId&filters[0].Condition=NUMBER.EQUAL&filters[0].Value=${task.id}&filters[1].Field=Type&filters[1].Condition=EQUAL&filters[1].Value=Instructor`
      );

      let transportMessagesPromise;
      if (hasTransportIncluded) {
        transportMessagesPromise = api.get(
          `/Communication?filters[0].Field=TaskId&filters[0].Condition=NUMBER.EQUAL&filters[0].Value=${task.id}&filters[1].Field=Type&filters[1].Condition=EQUAL&filters[1].Value=Transport`
        );
      } else {
        transportMessagesPromise = Promise.resolve({ data: [] });
      }

      const [instructorMessages, transportMessages] = await Promise.all([
        instructorMessagesPromise,
        transportMessagesPromise,
      ]);

      setMessages({
        Instructor: instructorMessages.data || [],
        Transport: transportMessages.data || [],
      });
    } catch (error) {
      Utils.logError(error);
      message.error('Não foi possível carregar as mensagens!');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (task?.id) {
      fetchMessages();
    }
  }, [task, hasTransportIncluded]);

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !canWrite || isMessagingDisabled) return;

    if (activeType === 'Instructor' && !canEffectivelySendToInstructor) {
      message.error(
        'Não é possível enviar mensagens para o instrutor porque não há instrutor selecionado.'
      );
      return;
    }

    if (activeType === 'Transport') {
      if (!canEffectivelySendToTransport) {
        message.error(
          'Não é possível enviar mensagens para o transporte porque não há empresa de transporte selecionada ou transporte não incluso.'
        );
        return;
      }
      if (isTransportCompanyUser && !isUserLinkedToTaskTransport) {
        message.error(
          'Você não pode enviar mensagens para este treinamento pois sua empresa de transporte não está vinculada a ele.'
        );
        return;
      }
    }

    try {
      setLoading(true);
      const from = userData.role?.name || 'Usuário';
      let to;
      if (isTransportCompanyUser || isOperatorUser) {
        to = 'Logistics';
      } else if (activeType === 'Instructor') {
        to = 'Instructor';
      } else {
        to = 'Transport';
      }

      const messageData = {
        TaskId: task.id,
        UserId: userData.id,
        UserName: userData.name,
        UserRole: userData.role?.name || '',
        Message: newMessage,
        Type: activeType,
        From: from,
        To: to,
      };

      await api.post('/Communication', messageData);
      fetchMessages();
      setNewMessage('');
    } catch (error) {
      Utils.logError(error);
      message.error('Não foi possível enviar a mensagem.');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey && canWrite) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const communicationTypesAvailable = [];
  communicationTypesAvailable.push({ value: 'Instructor', label: 'Instrutor' });
  if (hasTransportIncluded) {
    communicationTypesAvailable.push({ value: 'Transport', label: 'Transporte' });
  }

  const getAlertMessage = () => {
    if (activeType === 'Instructor' && !canEffectivelySendToInstructor) {
      return 'Não é possível enviar mensagens porque não há instrutor selecionado.';
    }
    if (activeType === 'Transport' && !canEffectivelySendToTransport) {
      return 'Não é possível enviar mensagens porque não há empresa de transporte selecionada.';
    }
    return null;
  };

  const alertMessage = getAlertMessage();

  return (
    <div className="communication-panel">
      <div className="communication-header">
        <Title level={5} className="communication-title">
          {activeType === 'Instructor' ? 'Comunicação Instrutor' : 'Comunicação Transporte'}
        </Title>

        {!isTransportCompanyUser &&
          (showRefreshButton || communicationTypesAvailable.length > 1) && (
            <div style={{ display: 'flex', alignItems: 'center' }}>
              {showRefreshButton && (
                <Button
                  icon={<ReloadOutlined />}
                  onClick={fetchMessages}
                  disabled={loading}
                  style={{ marginRight: 8 }}
                />
              )}
              {communicationTypesAvailable.length > 1 && !task.scheduleType && (
                <Select
                  value={activeType}
                  onChange={setActiveType}
                  className="communication-type-selector"
                  suffixIcon={<SwapOutlined />}
                  disabled={loading}
                  dropdownStyle={{ borderRadius: 16 }}
                >
                  {communicationTypesAvailable.map((option) => (
                    <Option key={option.value} value={option.value}>
                      {option.label}
                    </Option>
                  ))}
                </Select>
              )}
            </div>
          )}

        {isTransportCompanyUser && showRefreshButton && hasTransportIncluded && (
          <Button
            icon={<ReloadOutlined />}
            onClick={fetchMessages}
            disabled={loading}
            style={{ marginRight: 8 }}
          />
        )}
      </div>

      {alertMessage && (
        <Alert
          message={alertMessage}
          type="warning"
          showIcon
          icon={<InfoCircleOutlined />}
          className="custom-alert"
        />
      )}

      <div className="message-container">
        <MessageList
          messages={messages[activeType]}
          loading={loading}
          currentUserId={userData.id}
          activeType={activeType}
          task={task}
        />
      </div>

      {canWrite && (
        <div className="message-input-container">
          <Input.TextArea
            ref={messageInputRef}
            placeholder={isMessagingDisabled ? '' : 'Envie uma mensagem...'}
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            autoSize={{ minRows: 1, maxRows: 4 }}
            disabled={loading || isMessagingDisabled}
          />
          <Button
            type="primary"
            icon={<SendOutlined />}
            onClick={handleSendMessage}
            disabled={!newMessage.trim() || loading || isMessagingDisabled}
          />
        </div>
      )}
    </div>
  );
}

export default CommunicationPanel;
